import { forwardRef } from "react";
import { fileUrl, LOGO } from "@/lib/api";

const money = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB") : "—");

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
function words(n) {
  n = Math.floor(Number(n) || 0);
  if (n === 0) return "Zero";
  const two = (x) => (x < 20 ? ONES[x] : `${TENS[Math.floor(x / 10)]}${x % 10 ? " " + ONES[x % 10] : ""}`);
  const three = (x) => (x >= 100 ? `${ONES[Math.floor(x / 100)]} Hundred${x % 100 ? " " + two(x % 100) : ""}` : two(x));
  const parts = [];
  const crore = Math.floor(n / 1e7), lakh = Math.floor((n % 1e7) / 1e5), thou = Math.floor((n % 1e5) / 1e3), rest = n % 1e3;
  if (crore) parts.push(`${two(crore)} Crore`);
  if (lakh) parts.push(`${two(lakh)} Lakh`);
  if (thou) parts.push(`${two(thou)} Thousand`);
  if (rest) parts.push(three(rest));
  return parts.join(" ");
}
export const amountInWords = (n) => `${words(n)} Rupees`;

const STATUS_STYLE = { Paid: { bg: "#DCFCE7", fg: "#166534", label: "PAID" }, "Partially Paid": { bg: "#FEF3C7", fg: "#92400E", label: "PARTIALLY PAID" }, Pending: { bg: "#FEE2E2", fg: "#991B1B", label: "PENDING" } };

export const InvoiceDocument = forwardRef(function InvoiceDocument({ inv, settings }, ref) {
  const s = settings || {};
  const st = STATUS_STYLE[inv.payment_status] || STATUS_STYLE.Pending;
  const terms = (s.inv_terms || "").split("\n").filter(Boolean);
  const totalQty = (inv.items || []).reduce((a, i) => a + Number(i.qty || 0), 0);
  return (
    <div ref={ref} data-testid="invoice-document" style={{ width: "210mm", minHeight: "297mm", padding: "10mm 10mm 12mm", background: "#fff", color: "#111", fontFamily: "'Plus Jakarta Sans', Arial, sans-serif", fontSize: "11px", lineHeight: 1.35, position: "relative", boxSizing: "border-box" }}>
      <p style={{ fontSize: 10, fontWeight: 600, margin: 0 }}>Where Journeys Become Stories</p>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 6 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", maxWidth: "55%" }}>
          <img src={fileUrl(s.logo_url) || LOGO} alt="Safarshala" crossOrigin="anonymous" style={{ height: 40, width: "auto", marginTop: 4 }} />
          <div>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1466C4" }}>{s.inv_company_name || "Safarshala Tours"}</p>
            <p style={{ margin: 0, whiteSpace: "pre-line" }}>{s.inv_address}</p>
            {s.inv_gstin && <p style={{ margin: 0 }}><b>GSTIN :</b> {s.inv_gstin}</p>}
            <p style={{ margin: 0 }}><b>Mobile :</b> {s.inv_mobile}</p>
            <p style={{ margin: 0 }}><b>Email :</b> {s.inv_email}</p>
          </div>
        </div>
        <div style={{ width: "40%" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 800, letterSpacing: 0.5 }}>BILL OF SUPPLY</p>
            <span style={{ border: "1px solid #999", borderRadius: 3, padding: "2px 8px", fontSize: 9, color: "#555" }}>ORIGINAL</span>
          </div>
          <table style={{ width: "100%", marginTop: 8, fontSize: 11 }}><tbody>
            {[["Invoice No.", inv.invoice_no], ["Invoice Date", fmtDate(inv.invoice_date)], ["Due Date", fmtDate(inv.due_date)], inv.booking_id ? ["Booking ID", inv.booking_id] : null].filter(Boolean).map(([k, v]) => (
              <tr key={k}><td style={{ padding: "2px 0", width: "45%" }}>{k}</td><td style={{ width: "8%" }}>:</td><td style={{ textAlign: "right", fontWeight: 700 }} data-testid={k === "Invoice No." ? "doc-invoice-no" : undefined}>{v}</td></tr>
            ))}
          </tbody></table>
        </div>
      </div>

      <div style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <span style={{ display: "inline-block", background: "#DCD9F5", padding: "3px 14px", fontWeight: 700, fontSize: 10 }}>BILL TO</span>
          <p style={{ margin: "6px 0 0", fontWeight: 700, fontSize: 12 }}>{inv.customer?.name}</p>
          {inv.customer?.mobile && <p style={{ margin: 0 }}><b>Mobile :</b> {inv.customer.mobile}</p>}
          {inv.customer?.email && <p style={{ margin: 0 }}><b>Email :</b> {inv.customer.email}</p>}
          {inv.customer?.address && <p style={{ margin: 0, whiteSpace: "pre-line" }}>{inv.customer.address}</p>}
        </div>
        <div style={{ textAlign: "right" }}>
          <span data-testid="doc-status" style={{ display: "inline-block", background: st.bg, color: st.fg, fontWeight: 800, padding: "5px 14px", borderRadius: 4, fontSize: 12, letterSpacing: 1, border: `1px solid ${st.fg}33` }}>{st.label}</span>
          <div style={{ marginTop: 8, fontSize: 10.5, color: "#333" }}>
            <p style={{ margin: 0 }}><b>Trip :</b> {inv.trip?.name}</p>
            {inv.trip?.destination && <p style={{ margin: 0 }}><b>Destination :</b> {inv.trip.destination}</p>}
            <p style={{ margin: 0 }}><b>Departure :</b> {fmtDate(inv.trip?.departure_date)}{inv.trip?.return_date ? ` · Return : ${fmtDate(inv.trip.return_date)}` : ""}</p>
            <p style={{ margin: 0 }}><b>Travellers :</b> {inv.trip?.travellers}{inv.trip?.sharing ? ` (${inv.trip.sharing})` : ""}{inv.trip?.pickup ? ` · Pickup: ${inv.trip.pickup}` : ""}</p>
            <p style={{ margin: 0 }}><b>Booking Source :</b> {inv.source}</p>
          </div>
        </div>
      </div>

      <table style={{ width: "100%", marginTop: 14, borderCollapse: "collapse", fontSize: 11 }}>
        <thead><tr style={{ background: "#DCD9F5", fontWeight: 700 }}>
          {["S.NO.", "SERVICES", "QTY.", "RATE", "DISC.", "AMOUNT"].map((h, i) => <th key={h} style={{ padding: "6px 8px", textAlign: i >= 2 ? "right" : "left", fontSize: 10 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {(inv.items || []).map((it, i) => (
            <tr key={i} data-testid="doc-item">
              <td style={{ padding: "7px 8px", verticalAlign: "top", textAlign: "center", width: 40 }}>{i + 1}</td>
              <td style={{ padding: "7px 8px", verticalAlign: "top" }}><div style={{ fontWeight: 600, textTransform: "uppercase" }}>{it.description}</div>{it.sub && <div style={{ fontSize: 9.5, color: "#555" }}>{it.sub}</div>}</td>
              <td style={{ padding: "7px 8px", textAlign: "right", verticalAlign: "top" }}>{it.qty} PERSON</td>
              <td style={{ padding: "7px 8px", textAlign: "right", verticalAlign: "top" }}>{Number(it.rate).toLocaleString("en-IN")}</td>
              <td style={{ padding: "7px 8px", textAlign: "right", verticalAlign: "top" }}>{it.discount ? <>{Number(it.discount).toLocaleString("en-IN")}<div style={{ fontSize: 9, color: "#555" }}>({((it.discount / Math.max(1, it.qty * it.rate)) * 100).toFixed(2)}%)</div></> : "—"}</td>
              <td style={{ padding: "7px 8px", textAlign: "right", verticalAlign: "top", fontWeight: 600 }}>{Number(it.amount).toLocaleString("en-IN")}</td>
            </tr>
          ))}
          <tr style={{ height: Math.max(40, 160 - (inv.items?.length || 0) * 34) }}><td colSpan={6} /></tr>
          <tr style={{ background: "#DCD9F5", fontWeight: 700 }}>
            <td colSpan={2} style={{ padding: "6px 8px" }}>SUBTOTAL</td>
            <td style={{ padding: "6px 8px", textAlign: "right" }}>{totalQty}</td>
            <td />
            <td style={{ padding: "6px 8px", textAlign: "right" }}>{money(inv.total_discount)}</td>
            <td style={{ padding: "6px 8px", textAlign: "right" }}>{money(inv.final_total)}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 24, marginTop: 14 }}>
        <div style={{ width: "50%" }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 10.5 }}>TERMS AND CONDITIONS</p>
          <ol style={{ margin: "4px 0 0", paddingLeft: 14, fontSize: 10 }}>{terms.map((t, i) => <li key={i}>{t}</li>)}</ol>
          <p style={{ margin: "10px 0 0", fontWeight: 700, fontSize: 10.5 }}>BANK DETAILS</p>
          <table style={{ fontSize: 10, marginTop: 2 }}><tbody>
            {[["Name:", s.inv_bank_holder], ["IFSC Code:", s.inv_bank_ifsc], ["Account No:", s.inv_bank_account], ["Bank:", s.inv_bank_name]].map(([k, v]) => v ? <tr key={k}><td style={{ paddingRight: 14, color: "#333" }}>{k}</td><td>{v}</td></tr> : null)}
          </tbody></table>
          {(s.upi_id || s.qr_url) && (
            <div style={{ marginTop: 10 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 10.5 }}>PAYMENT QR CODE</p>
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginTop: 4 }}>
                <div style={{ fontSize: 10 }}>{s.upi_id && <><p style={{ margin: 0 }}>UPI ID:</p><p style={{ margin: 0, fontWeight: 600 }}>{s.upi_id}</p></>}</div>
                {s.qr_url && <img src={fileUrl(s.qr_url)} alt="UPI QR" crossOrigin="anonymous" style={{ height: 64, width: 64 }} />}
              </div>
            </div>
          )}
        </div>
        <div style={{ width: "44%" }}>
          <table style={{ width: "100%", fontSize: 11 }}><tbody>
            {inv.extra_discount > 0 && <tr><td style={{ padding: "3px 0", color: "#333" }}>Subtotal</td><td style={{ textAlign: "right" }}>{money(inv.subtotal)}</td></tr>}
            {inv.extra_discount > 0 && <tr><td style={{ padding: "3px 0", color: "#333" }}>Additional Discount{inv.notes ? ` (${inv.notes})` : ""}</td><td style={{ textAlign: "right" }}>− {money(inv.extra_discount)}</td></tr>}
            <tr style={{ borderTop: "1px solid #999", borderBottom: "1px solid #999" }}><td style={{ padding: "5px 0", fontWeight: 700 }}>Total Amount</td><td style={{ textAlign: "right", fontWeight: 700 }} data-testid="doc-total">{money(inv.final_total)}</td></tr>
            <tr><td style={{ padding: "4px 0" }}>Received Amount</td><td style={{ textAlign: "right" }} data-testid="doc-paid">{money(inv.amount_paid)}</td></tr>
            <tr><td style={{ padding: "2px 0", fontWeight: 700 }}>Balance</td><td style={{ textAlign: "right", fontWeight: 700 }} data-testid="doc-balance">{money(inv.pending_amount)}</td></tr>
            <tr><td style={{ padding: "4px 0", color: "#333" }}>Payment Method</td><td style={{ textAlign: "right" }}>{inv.payment_method || "—"}{inv.transaction_ref ? ` · Ref ${inv.transaction_ref}` : ""}</td></tr>
          </tbody></table>
          <div style={{ textAlign: "right", marginTop: 12 }}>
            <p style={{ margin: 0, fontWeight: 700 }}>Total Amount (in words)</p>
            <p style={{ margin: 0 }}>{amountInWords(inv.final_total)}</p>
          </div>
          <div style={{ textAlign: "right", marginTop: 26 }}>
            {s.inv_signature_url ? <img src={fileUrl(s.inv_signature_url)} alt="Signature" crossOrigin="anonymous" style={{ height: 48, display: "inline-block" }} /> : <div style={{ height: 48 }} />}
            <p style={{ margin: "6px 0 0", fontWeight: 700 }}>Authorised Signature for</p>
            <p style={{ margin: 0, fontWeight: 700 }}>{s.inv_company_name || "Safarshala Tours"}</p>
          </div>
        </div>
      </div>

      <div style={{ position: "absolute", left: "10mm", right: "10mm", bottom: "8mm", borderTop: "1px solid #ddd", paddingTop: 6, fontSize: 9.5, color: "#555", display: "flex", justifyContent: "space-between" }}>
        <span>Cancellation / refund as per the terms above. This is a computer-generated bill of supply.</span>
        <span>Support: {s.inv_mobile || s.whatsapp_number} · {s.inv_email}</span>
      </div>
    </div>
  );
});
